import React from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck, Send, Search, Users, CheckCircle,
  Coins, Calendar, Star, AlertTriangle, Banknote
} from 'lucide-react';

const PayoutModelSection = () => {
  const rules = [
    {
      icon: ShieldCheck,
      title: 'VERIFIED SIDE QUESTERS',
      description: 'Identity + device/environment verification required before submissions.',
      color: 'text-success-green',
    },
    {
      icon: Send,
      title: 'SUBMIT',
      description: 'Upload your video directly through your account and submit.',
      color: 'text-neon-pink',
    },
    {
      icon: Search,
      title: 'INTERNAL QA',
      description: 'Our team reviews for quality, clarity, and compliance.',
      color: 'text-lavender',
    },
    {
      icon: Users,
      title: 'PARTNER REVIEW',
      description: 'Partners validate data usefulness for their specific needs.',
      color: 'text-trophy-gold',
    },
    {
      icon: CheckCircle,
      title: 'ACCEPTANCE',
      description: 'Approved data counts toward your score and earnings.',
      color: 'text-success-green',
    },
  ];

  const payoutRules = [
    {
      icon: Banknote,
      text: 'Earnings ONLY for accepted data submissions',
      highlight: true,
    },
    {
      icon: Calendar,
      text: 'Payouts processed after partner payment confirmation',
      highlight: false,
    },
    {
      icon: Star,
      text: 'Monthly bonus rewards for top contributors (supplemental, not guaranteed)',
      highlight: false,
    },
    {
      icon: AlertTriangle,
      text: 'NO payment for rejected or non-compliant data',
      highlight: true,
      warning: true,
    },
  ];

  return (
    <section id="payout-model" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <Coins className="w-6 h-6 text-trophy-gold" />
            <h2 className="font-pixel text-lg sm:text-xl md:text-2xl text-trophy-gold">
              CONTRIBUTOR & PAYOUT MODEL
            </h2>
            <Coins className="w-6 h-6 text-trophy-gold" />
          </div>
          <p className="font-terminal text-xl text-lavender">
            Transparent rules for earning cash rewards
          </p>
        </motion.div>

        {/* Review Flow */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="arcade-panel rounded-lg p-6 sm:p-8 mb-8"
        >
          <h3 className="font-pixel text-sm text-neon-pink text-center mb-8">
            📋 REVIEW FLOW
          </h3>
          
          {/* Flow Steps */}
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-2">
            {rules.map((rule, index) => (
              <React.Fragment key={rule.title}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col items-center text-center w-full sm:w-auto sm:flex-1 max-w-[150px]"
                >
                  <div className="w-14 h-14 bg-panel-purple rounded-lg flex items-center justify-center border-2 border-dark-border mb-3 hover:shadow-glow-pink transition-shadow">
                    <rule.icon className={`w-7 h-7 ${rule.color}`} />
                  </div>
                  <span className="font-pixel text-[8px] text-foreground">{rule.title}</span>
                </motion.div>
                
                {/* Arrow between steps */}
                {index < rules.length - 1 && (
                  <div className="hidden sm:block text-neon-pink font-pixel text-lg">→</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* Payout Rules Board */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="arcade-panel rounded-lg overflow-hidden"
        >
          <div className="bg-trophy-gold/20 px-6 py-4 border-b-4 border-dark-border">
            <h3 className="font-pixel text-sm text-trophy-gold text-center flex items-center justify-center gap-2">
              <Banknote className="w-5 h-5" />
              PAYOUT RULES
            </h3>
          </div>
          
          <div className="p-6 space-y-4">
            {payoutRules.map((rule, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`flex items-center gap-4 p-4 rounded border-l-4 ${
                  rule.warning 
                    ? 'border-warning-orange bg-warning-orange/10' 
                    : rule.highlight 
                      ? 'border-success-green bg-success-green/10' 
                      : 'border-panel-purple bg-panel-purple/30'
                }`}
              >
                <rule.icon className={`w-6 h-6 flex-shrink-0 ${
                  rule.warning ? 'text-warning-orange' : rule.highlight ? 'text-success-green' : 'text-lavender'
                }`} />
                <span className="font-terminal text-lg text-foreground">{rule.text}</span>
              </motion.div>
            ))}
          </div>

          {/* Important Note */}
          <div className="px-6 pb-6">
            <div className="p-4 bg-deep-purple rounded border border-neon-pink/30">
              <p className="font-terminal text-base text-muted text-center">
                <AlertTriangle className="inline-block w-4 h-4 text-warning-orange mr-2" />
                This is <span className="text-warning-orange">NOT guaranteed income</span>. 
                Earnings depend on submission quality, acceptance rate, and partner needs.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PayoutModelSection;
